import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, filter, map, startWith, switchMapTo, takeUntil, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';

import { UnsubscribeController } from '@personal-area/global/common'
import { TariffsFacade } from '@personal-area/global/accounts/services';
import { AccountTariff, Tariff } from '@personal-area/global/accounts/models';
import { UserFacade } from '@personal-area/global/users/services';
import { Type } from '@personal-area/global/users/models';

const EXCLUDED_TARIFFS = [
  AccountTariff.partner,
];

const isTariffVisible =
  (selectedType: Type, whiteList: string[]) =>
    (tariff: Tariff): boolean => {
      if (whiteList.length && !whiteList.includes(tariff.type)) {
        return false;
      }
      if (selectedType === Type.Demo) {
        return tariff.isDemo;
      }
      return !EXCLUDED_TARIFFS.includes(tariff.type) && !tariff.isDemo;
    }

@Component({
    selector: 'pac-account-tariffs',
    templateUrl: './account-tariffs.component.html',
    styleUrls: ['./account-tariffs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccountTariffsComponent extends UnsubscribeController implements OnInit, OnDestroy {
  @Input() accountType = Type.Trader;
  @Input() isToggleAvailable= true;
  @Input() availableTariffs: string[] = [];

  public email$: Observable<string>;
  public tariffs$ = new BehaviorSubject<Tariff[]>(null);
  public isIslamicCountry: boolean;
  public isSwapFreeAvailable = environment.isSwapFreeAvailable;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _userFacade: UserFacade,
    private _translateService: TranslateService,
    private _tariffsFacade: TariffsFacade,
  ) {
    super();
    this.email$ = this._userFacade.user$.pipe(map(({ email }) => email));
    this.isIslamicCountry = environment.islamicCountries.includes(
      this._userFacade.loggedUser.country
    );
  }

  public ngOnInit(): void {
    this._translateService.onLangChange
      .pipe(
        startWith(null),
        tap(() => this._tariffsFacade.getTariffs(this._userFacade.loggedUser.id)),
        switchMapTo(this._tariffsFacade.isLoaded$),
        filter(Boolean),
        switchMapTo(this._tariffsFacade.tariffs$),
        map(items => items
          .filter(isTariffVisible(this.accountType, this.availableTariffs))
          .sort((a, b) => a.sort - b.sort)
        ),
        takeUntil(this._unsubscribe),
      )
      .subscribe(tariffs => {
        if (tariffs.length === 0) {
          this._router.navigateByUrl('/dashboard');
          return;
        }

        this.tariffs$.next(tariffs);
      });
  }

  public selectTariff(type: AccountTariff): void {
    this._router.navigate([type], {
      queryParamsHandling: 'preserve',
      relativeTo: this._route,
    });
  }
}
